import { lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router';
import { PortalLayout } from './layout/PortalLayout.tsx';
import LoginPage from './pages/LoginPage.tsx';
import InvitePage from './pages/InvitePage.tsx';
import HandoffPage from './pages/HandoffPage.tsx';
import DashboardPage from './pages/DashboardPage.tsx';
import NotFoundPage, { RouteErrorPage } from './pages/NotFoundPage.tsx';

// Each page is its own chunk (the news editor also pulls in TipTap); PortalLayout shows a spinner while one loads.
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage.tsx'));
const BannersPage = lazy(() => import('./pages/BannersPage.tsx'));
const NewsListPage = lazy(() => import('./pages/NewsListPage.tsx'));
const NewsEditorPage = lazy(() => import('./pages/NewsEditorPage.tsx'));
const NewsCategoriesPage = lazy(() => import('./pages/NewsCategoriesPage.tsx'));
const LevelsPage = lazy(() => import('./pages/LevelsPage.tsx'));
const MembersPage = lazy(() => import('./pages/MembersPage.tsx'));
const ReferralsPage = lazy(() => import('./pages/ReferralsPage.tsx'));
const DeletionRequestsPage = lazy(() => import('./pages/DeletionRequestsPage.tsx'));
const ReportsPage = lazy(() => import('./pages/ReportsPage.tsx'));
const RewardsPage = lazy(() => import('./pages/RewardsPage.tsx'));
const StaffPage = lazy(() => import('./pages/StaffPage.tsx'));
const WalletPage = lazy(() => import('./pages/WalletPage.tsx'));
const AboutPage = lazy(() => import('./pages/AboutPage.tsx'));
const PointsRulesPage = lazy(() => import('./pages/program/PointsRulesPage.tsx'));
const RewardEditorPage = lazy(() => import('./pages/program/RewardEditorPage.tsx'));
const StampsPage = lazy(() => import('./pages/program/StampsPage.tsx'));
const MemberGiftsPage = lazy(() => import('./pages/program/MemberGiftsPage.tsx'));
const CounterPage = lazy(() => import('./pages/program/CounterPage.tsx'));
const TransactionsPage = lazy(() => import('./pages/program/TransactionsPage.tsx'));
const PosPage = lazy(() => import('./pages/PosPage.tsx'));
const CampaignPlannerPage = lazy(() => import('./pages/ai/CampaignPlannerPage.tsx'));
const MemberValuePage = lazy(() => import('./pages/ai/MemberValuePage.tsx'));
const ForecastPage = lazy(() => import('./pages/ai/ForecastPage.tsx'));
const WeeklyReportsPage = lazy(() => import('./pages/ai/WeeklyReportsPage.tsx'));
const AiSetupPage = lazy(() => import('./pages/ai/AiSetupPage.tsx'));
const NewTransactionPage = lazy(() => import('./pages/program/NewTransactionPage.tsx'));
// Plan modules (each page also checks its module; the nav hides pages the plan does not include).
const PromotionsPage = lazy(() => import('./pages/program/PromotionsPage.tsx'));
const CampaignEditorPage = lazy(() => import('./pages/program/CampaignEditorPage.tsx'));
const ReferralRewardsPage = lazy(() => import('./pages/program/ReferralRewardsPage.tsx'));
const AutomationsPage = lazy(() => import('./pages/program/AutomationsPage.tsx'));
const PaidMembershipPage = lazy(() => import('./pages/program/PaidMembershipPage.tsx'));
const StoredValuePage = lazy(() => import('./pages/program/StoredValuePage.tsx'));
const GiftCardsPage = lazy(() => import('./pages/program/GiftCardsPage.tsx'));
const SegmentsPage = lazy(() => import('./pages/SegmentsPage.tsx'));
const NewslettersPage = lazy(() => import('./pages/newsletter/NewslettersPage.tsx'));
const NewsletterEditorPage = lazy(() => import('./pages/newsletter/NewsletterEditorPage.tsx'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage.tsx'));
const ShopReportPage = lazy(() => import('./pages/ShopReportPage.tsx'));
const PlanPage = lazy(() => import('./pages/PlanPage.tsx'));
const SecurityPage = lazy(() => import('./pages/SecurityPage.tsx'));

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteErrorPage /> },
  { path: '/invite', element: <InvitePage />, errorElement: <RouteErrorPage /> },
  // Platform admin portal 「登入品牌後台」 one-time link.
  { path: '/handoff', element: <HandoffPage />, errorElement: <RouteErrorPage /> },
  {
    path: '/',
    element: <PortalLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'banners', element: <BannersPage /> },
      { path: 'news', element: <NewsListPage /> },
      { path: 'news/new', element: <NewsEditorPage /> },
      { path: 'news/:id', element: <NewsEditorPage /> },
      { path: 'news-categories', element: <NewsCategoriesPage /> },
      { path: 'about', element: <AboutPage /> },
      { path: 'levels', element: <LevelsPage /> },
      { path: 'points-rules', element: <PointsRulesPage /> },
      // 優惠券派發記錄 and 批量優惠碼 now live on the 優惠券 pages (LPSS-12/13).
      { path: 'coupon-issues', element: <Navigate to="/rewards?view=issues" replace /> },
      { path: 'stamps', element: <StampsPage /> },
      { path: 'stamps/:id', element: <StampsPage /> },
      { path: 'member-gifts', element: <MemberGiftsPage /> },
      { path: 'loyalty', element: <CounterPage /> },
      { path: 'members', element: <MembersPage /> },
      { path: 'transactions', element: <TransactionsPage /> },
      { path: 'transactions/new', element: <NewTransactionPage /> },
      { path: 'pos', element: <PosPage /> },
      { path: 'wallet', element: <WalletPage /> },
      { path: 'referrals', element: <ReferralsPage /> },
      { path: 'deletion-requests', element: <DeletionRequestsPage /> },
      { path: 'reports', element: <ReportsPage /> },
      { path: 'rewards', element: <RewardsPage /> },
      { path: 'rewards/new', element: <RewardEditorPage /> },
      { path: 'rewards/:id', element: <RewardEditorPage /> },
      { path: 'staff', element: <StaffPage /> },
      { path: 'promotions', element: <PromotionsPage /> },
      { path: 'promotions/new', element: <CampaignEditorPage /> },
      { path: 'promotions/:id', element: <CampaignEditorPage /> },
      { path: 'referral-rewards', element: <ReferralRewardsPage /> },
      { path: 'code-batches', element: <Navigate to="/rewards" replace /> },
      { path: 'automations', element: <AutomationsPage /> },
      { path: 'paid-membership', element: <PaidMembershipPage /> },
      { path: 'stored-value', element: <StoredValuePage /> },
      { path: 'gift-cards', element: <GiftCardsPage /> },
      { path: 'segments', element: <SegmentsPage /> },
      { path: 'newsletters', element: <NewslettersPage /> },
      { path: 'newsletters/:id', element: <NewsletterEditorPage /> },
      { path: 'analytics', element: <AnalyticsPage /> },
      { path: 'ai/campaign-planner', element: <CampaignPlannerPage /> },
      { path: 'ai/member-value', element: <MemberValuePage /> },
      { path: 'ai/forecast', element: <ForecastPage /> },
      { path: 'ai/weekly-reports', element: <WeeklyReportsPage /> },
      { path: 'ai/setup', element: <AiSetupPage /> },
      { path: 'shop-report', element: <ShopReportPage /> },
      { path: 'plan', element: <PlanPage /> },
      { path: 'security', element: <SecurityPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
